/**
 * EmailSettingsPanel · v2.7.45+E4 (2026-02-22)
 * ═════════════════════════════════════════════════════════════════════
 * Right-side drawer that manages the calling user's corporate email
 * mailboxes. Mounted from EmailClientPage when:
 *   • `?settings=1` is in the URL (sidebar "MAIL ACCOUNT SETTINGS")
 *   • The "Mail Account Settings" button in the column-1 footer is clicked
 *   • The "+" Add Account button in the column-1 header is clicked
 *
 * Functionality:
 *   • List every mailbox (parent + aliases) with health dot + last-tested badge
 *   • Test connection (per-mailbox) — re-uses POST /accounts/{id}/test
 *   • Toggle active / mark as default
 *   • Edit label · IMAP host/port · SMTP host/port · alias_of_email
 *   • Reset password (re-encrypts via PATCH password=)
 *   • Delete mailbox
 *   • Add new mailbox (parent w/ password OR alias linking to a parent)
 *
 * Backend endpoints used (all already shipped in routers/email.py):
 *   GET    /api/admin/email/accounts
 *   POST   /api/admin/email/accounts
 *   PATCH  /api/admin/email/accounts/{id}
 *   DELETE /api/admin/email/accounts/{id}
 *   POST   /api/admin/email/accounts/{id}/test
 */
import { useEffect, useMemo, useState } from "react";
import adminApi from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import SignaturesSection from "./SignaturesSection";
import GlobalDisclaimerEditor from "./GlobalDisclaimerEditor";
import { BISON_CORPORATE_EMAIL_LOGO, TAXI_COMMANDER_LOGO } from "@/lib/brandAssets";
import {
  X, Plus, Check, ShieldAlert, Star, StarOff,
  Trash2, Server, Loader2, Mail, RefreshCw,
} from "lucide-react";

const IONOS_DEFAULTS = {
  imap_host: "imap.ionos.com",
  imap_port: 993,
  smtp_host: "smtp.ionos.com",
  smtp_port: 465,
};

export default function EmailSettingsPanel({ accounts, onClose, onChanged, adminRole }) {
  const [busy, setBusy] = useState(null); // accountId currently being mutated
  const [editingId, setEditingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [tab, setTab] = useState("mailboxes"); // mailboxes | signatures | disclaimer
  const [signatures, setSignatures] = useState([]);
  const [mailboxSigMap, setMailboxSigMap] = useState({}); // {mailbox_id: signature_id}
  const parentEmails = useMemo(
    () => accounts.filter((a) => !a.is_alias).map((a) => a.email),
    [accounts],
  );

  // Load signatures + per-mailbox default-signature mappings whenever
  // the drawer opens. Cached locally so the mailbox dropdowns are snappy.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: sigList } = await adminApi.get("/admin/email/signatures");
        if (!cancelled) setSignatures(sigList?.items || []);
        // Pull the per-mailbox mapping in parallel
        const entries = await Promise.all(accounts.map(async (a) => {
          try {
            const { data } = await adminApi.get(`/admin/email/accounts/${a.id}/signature`);
            return [a.id, data?.default_signature_id || ""];
          } catch { return [a.id, ""]; }
        }));
        if (!cancelled) setMailboxSigMap(Object.fromEntries(entries));
      } catch { /* silent — signatures are optional */ }
    })();
    return () => { cancelled = true; };
  }, [accounts]);

  const setMailboxSignature = async (mailboxId, sigId) => {
    try {
      await adminApi.put(`/admin/email/accounts/${mailboxId}/signature`, {
        default_signature_id: sigId || null,
      });
      setMailboxSigMap((m) => ({ ...m, [mailboxId]: sigId }));
      toast.success("Signature mapping saved");
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const refresh = () => onChanged && onChanged();

  const onTest = async (id) => {
    setBusy(id);
    try {
      const { data } = await adminApi.post(`/admin/email/accounts/${id}/test`);
      if (data?.ok) toast.success(`Connection OK · ${data.imap ? "IMAP ✓" : "IMAP ✗"} · ${data.smtp ? "SMTP ✓" : "SMTP ✗"}`);
      else toast.error(`Connection failed: ${data?.error || "unknown"}`);
      refresh();
    } catch (e) {
      toast.error(`Test failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(null);
    }
  };

  const onPatch = async (id, payload) => {
    setBusy(id);
    try {
      await adminApi.patch(`/admin/email/accounts/${id}`, payload);
      toast.success("Saved");
      refresh();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(null);
    }
  };

  const onDelete = async (id, label, isPrimary) => {
    // Client-side guard rails matched to the backend safeguards. Warn
    // the operator up front so they don't hit a 400 and lose context.
    const totalAccounts = accounts.length;
    if (totalAccounts <= 1) {
      toast.error("Cannot remove the last email account — you must retain at least one verified mailbox.");
      return;
    }
    if (isPrimary) {
      toast.error("Promote another verified mailbox to primary first (star icon), then retry the delete.");
      return;
    }
    if (!window.confirm(`Delete mailbox "${label}"? This removes it from the console only — your IONOS mailbox is untouched.`)) return;
    setBusy(id);
    try {
      await adminApi.delete(`/admin/email/accounts/${id}`);
      toast.success(`Removed ${label}`);
      refresh();
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="email-settings-panel">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} data-testid="email-settings-backdrop"/>

      {/* Drawer */}
      <aside
        className="relative w-full max-w-2xl h-full bg-white dark:bg-zinc-950 border-l border-slate-200 dark:border-zinc-800 overflow-y-auto shadow-2xl"
        data-testid="email-settings-drawer"
      >
        <header className="sticky top-0 z-10 bg-white dark:bg-zinc-950 border-b border-slate-200 dark:border-zinc-800 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {/* v2.7.48+E23 · Dual-logo header — Taxi Commander +
                Bison Email side-by-side, symmetrical 50 % size cut
                vs the previous solo-Bison plate. */}
            <div
              className="bg-slate-50 dark:bg-zinc-950 flex-shrink-0 overflow-hidden flex items-center justify-center gap-2 sm:gap-3"
              data-testid="email-settings-bison-brand"
              style={{ padding: 0, margin: 0 }}
            >
              <img
                src={TAXI_COMMANDER_LOGO}
                alt="Taxi Commander · Admin Console Software"
                className="block h-12 sm:h-14 w-auto object-contain"
                style={{ maxWidth: "50%", margin: 0, padding: 0 }}
                data-testid="email-settings-taxi-commander-brand"
              />
              <img
                src={BISON_CORPORATE_EMAIL_LOGO}
                alt="Bison Corporate Email System"
                className="block h-12 sm:h-14 w-auto object-contain"
                style={{ maxWidth: "50%", margin: 0, padding: 0 }}
              />
            </div>
            <Server className="h-5 w-5 text-sky-500"/>
            <div>
              <div className="text-base font-extrabold tracking-wide text-slate-900 dark:text-zinc-100">Mail Account Settings</div>
              <div className="text-[11px] text-slate-500 dark:text-zinc-400">Manage IMAP / SMTP mailboxes &amp; aliases</div>
            </div>
          </div>
          <Button
            variant="ghost" size="sm"
            className="h-8 w-8 p-0 text-slate-600 dark:text-zinc-400"
            onClick={onClose}
            data-testid="email-settings-close"
            aria-label="Close settings"
          >
            <X className="h-4 w-4"/>
          </Button>
        </header>

        {/* v2.7.45+E7 · Tab switcher (spec §2/§3 surface area) */}
        <nav className="px-5 py-2 border-b border-slate-200 dark:border-zinc-800 bg-slate-50/40 dark:bg-zinc-900/30 flex gap-1">
          {[
            { id: "mailboxes",  label: "Mailboxes" },
            { id: "signatures", label: "Signatures" },
            { id: "disclaimer", label: "Global Disclaimer" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`settings-tab-${t.id}`}
              className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest rounded transition-colors ${
                tab === t.id
                  ? "bg-sky-600 text-white"
                  : "text-slate-700 dark:text-zinc-300 hover:bg-slate-200 dark:hover:bg-zinc-800"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div className="p-5 space-y-4">
          {tab === "mailboxes" && <>
          {/* Add account CTA */}
          <div>
            {!adding ? (
              <Button
                onClick={() => setAdding(true)}
                className="bg-sky-600 hover:bg-sky-500 text-white font-bold gap-2"
                data-testid="email-settings-add-btn"
              >
                <Plus className="h-4 w-4"/>Add Mailbox
              </Button>
            ) : (
              <AddAccountForm
                parentEmails={parentEmails}
                onCancel={() => setAdding(false)}
                onSaved={() => { setAdding(false); refresh(); }}
              />
            )}
          </div>

          {/* Existing accounts */}
          <div className="space-y-3">
            {accounts.length === 0 && (
              <div className="text-sm text-slate-500 dark:text-zinc-500 text-center py-6 border border-dashed border-slate-300 dark:border-zinc-700 rounded">
                No mailboxes configured yet. Click <span className="font-bold">Add Mailbox</span> to get started.
              </div>
            )}
            {accounts.map((a) => (
              <AccountRow
                key={a.id}
                account={a}
                isEditing={editingId === a.id}
                busy={busy === a.id}
                parentEmails={parentEmails}
                signatures={signatures}
                defaultSignatureId={mailboxSigMap[a.id] || ""}
                onSetSignature={(sigId) => setMailboxSignature(a.id, sigId)}
                onStartEdit={() => setEditingId(a.id)}
                onCancelEdit={() => setEditingId(null)}
                onTest={() => onTest(a.id)}
                onPatch={(payload) => onPatch(a.id, payload)}
                onSaveEdit={async (payload) => {
                  await onPatch(a.id, payload);
                  setEditingId(null);
                }}
                onDelete={() => onDelete(a.id, a.label || a.email, !!a.is_default)}
                totalAccounts={accounts.length}
              />
            ))}
          </div>
          </>}

          {tab === "signatures" && (
            <SignaturesSection adminRole={adminRole}/>
          )}

          {tab === "disclaimer" && (
            <GlobalDisclaimerEditor adminRole={adminRole}/>
          )}

          {/* Help section — only relevant in the mailbox tab */}
          {tab === "mailboxes" && (
            <div className="mt-6 p-3 rounded border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/20 text-[11px] text-sky-900 dark:text-sky-200">
              <div className="font-bold mb-1">IONOS Mailbox Tips</div>
              <ul className="list-disc list-inside space-y-0.5">
                <li>IMAP: <code className="font-mono">imap.ionos.com:993</code> (SSL/TLS 1.2+)</li>
                <li>SMTP: <code className="font-mono">smtp.ionos.com:465</code> (SSL/TLS 1.2+)</li>
                <li>For shared addresses (e.g. <code className="font-mono">dispatch@</code>) that route to a single mailbox, add them as an <b>alias</b> pointing at the parent — no separate password needed.</li>
                <li>Passwords are encrypted at rest via Fernet. Use the Test button after any change to verify.</li>
              </ul>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// AccountRow — list item + inline editor
// ─────────────────────────────────────────────────────────────────────
function AccountRow({
  account, isEditing, busy, parentEmails,
  signatures = [], defaultSignatureId = "", onSetSignature = () => {},
  onStartEdit, onCancelEdit, onTest, onPatch, onSaveEdit, onDelete,
  totalAccounts = 1,
}) {
  const health = account.last_health || {};
  const healthCls =
    health.ok === true  ? "bg-emerald-500" :
    health.ok === false ? "bg-rose-500"    :
                          "bg-slate-400";
  const healthText =
    health.ok === true  ? "Healthy" :
    health.ok === false ? (health.error || "Connection error") :
                          "Untested";

  return (
    <div
      className="border border-slate-200 dark:border-zinc-800 rounded bg-slate-50 dark:bg-zinc-900/40"
      data-testid={`email-settings-row-${account.email}`}
    >
      {/* Row header */}
      <div className="px-3 py-3 flex items-start gap-3">
        <span className={`mt-1.5 inline-block h-2.5 w-2.5 rounded-full flex-shrink-0 ${healthCls}`} title={healthText}/>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-bold text-slate-900 dark:text-zinc-100 truncate">{account.label || account.email}</div>
            {account.is_default && (
              <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                Default
              </span>
            )}
            {account.is_alias && (
              <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                Alias
              </span>
            )}
            {!account.active && (
              <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300">
                Inactive
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 dark:text-zinc-400 font-mono truncate">{account.email}</div>
          {account.is_alias && account.alias_of_email && (
            <div className="text-[10px] text-amber-600 dark:text-amber-400 italic mt-0.5">
              ↳ inherits credentials from {account.alias_of_email}
            </div>
          )}
          <div className="text-[10px] text-slate-500 dark:text-zinc-500 mt-1 flex items-center gap-2">
            <Server className="h-3 w-3"/>
            IMAP: {account.imap_host}:{account.imap_port}
            <span className="text-slate-300 dark:text-zinc-700">·</span>
            SMTP: {account.smtp_host}:{account.smtp_port}
          </div>
          {health.ok === false && health.error && (
            <div className="text-[10px] text-rose-600 dark:text-rose-300 mt-1 flex items-center gap-1">
              <ShieldAlert className="h-3 w-3"/>
              {health.error}
            </div>
          )}
          {/* v2.7.45+E7 · Per-mailbox default signature (spec §4) */}
          <div className="mt-2 flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400 whitespace-nowrap">
              Default signature:
            </span>
            <select
              value={defaultSignatureId || ""}
              onChange={(e) => onSetSignature(e.target.value)}
              className="h-7 flex-1 max-w-xs rounded border border-slate-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-1.5 text-[11px]"
              data-testid={`email-settings-sig-${account.email}`}
            >
              <option value="">— None —</option>
              {signatures.map((s) => (
                <option key={s.id} value={s.id}>{s.name}{s.is_system ? " · system" : s.shared ? " · team" : ""}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Quick actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            variant="outline" size="sm"
            className="h-7 px-2 text-[10px] gap-1"
            onClick={onTest}
            disabled={busy}
            data-testid={`email-settings-test-${account.email}`}
            title="Test IMAP + SMTP connection"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin"/> : <Check className="h-3 w-3"/>}
            Test
          </Button>
          <Button
            variant="outline" size="sm"
            className="h-7 px-2 text-[10px]"
            onClick={() => onPatch({ is_default: !account.is_default })}
            disabled={busy || account.is_default}
            data-testid={`email-settings-default-${account.email}`}
            title={account.is_default ? "Already the default sender" : "Set as default sender"}
          >
            {account.is_default
              ? <Star className="h-3 w-3 text-emerald-500 fill-emerald-500"/>
              : <StarOff className="h-3 w-3"/>}
          </Button>
          <Switch
            checked={!!account.active}
            onCheckedChange={(checked) => onPatch({ active: checked })}
            disabled={busy}
            data-testid={`email-settings-active-${account.email}`}
            title={account.active ? "Active — included in unread polling" : "Inactive"}
          />
          {!isEditing ? (
            <Button
              variant="ghost" size="sm"
              className="h-7 px-2 text-[10px]"
              onClick={onStartEdit}
              data-testid={`email-settings-edit-${account.email}`}
            >
              Edit
            </Button>
          ) : (
            <Button
              variant="ghost" size="sm"
              className="h-7 px-2 text-[10px]"
              onClick={onCancelEdit}
            >
              Cancel
            </Button>
          )}
          {(() => {
            const isLast = totalAccounts <= 1;
            const isPrimaryBlock = !!account.is_default && !isLast;
            const disabled = busy || isLast || isPrimaryBlock;
            const title = isLast
              ? "Cannot remove — this is your last mailbox"
              : isPrimaryBlock
                ? "Promote another mailbox to primary first, then delete this one"
                : "Delete mailbox";
            return (
              <Button
                variant="ghost" size="sm"
                className="h-7 w-7 p-0 text-rose-600 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-900/20 disabled:opacity-40 disabled:cursor-not-allowed"
                onClick={onDelete}
                disabled={disabled}
                data-testid={`email-settings-delete-${account.email}`}
                title={title}
                aria-label={title}
              >
                <Trash2 className="h-3.5 w-3.5"/>
              </Button>
            );
          })()}
        </div>
      </div>

      {/* Inline editor */}
      {isEditing && (
        <EditAccountForm
          account={account}
          parentEmails={parentEmails}
          onSave={onSaveEdit}
          busy={busy}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// EditAccountForm — inline editor on a row
// ─────────────────────────────────────────────────────────────────────
function EditAccountForm({ account, parentEmails, onSave, busy }) {
  const [label, setLabel] = useState(account.label || "");
  const [imapHost, setImapHost] = useState(account.imap_host || IONOS_DEFAULTS.imap_host);
  const [imapPort, setImapPort] = useState(String(account.imap_port || IONOS_DEFAULTS.imap_port));
  const [smtpHost, setSmtpHost] = useState(account.smtp_host || IONOS_DEFAULTS.smtp_host);
  const [smtpPort, setSmtpPort] = useState(String(account.smtp_port || IONOS_DEFAULTS.smtp_port));
  const [aliasOf, setAliasOf] = useState(account.alias_of_email || "");
  const [newPassword, setNewPassword] = useState("");

  const isAlias = !!aliasOf;
  const otherParents = parentEmails.filter((e) => e !== account.email);

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      label,
      imap_host: imapHost,
      imap_port: parseInt(imapPort, 10) || IONOS_DEFAULTS.imap_port,
      smtp_host: smtpHost,
      smtp_port: parseInt(smtpPort, 10) || IONOS_DEFAULTS.smtp_port,
      alias_of_email: aliasOf || "",
    };
    if (newPassword) payload.password = newPassword;
    onSave(payload);
  };

  return (
    <form
      onSubmit={submit}
      className="border-t border-slate-200 dark:border-zinc-800 px-4 py-3 space-y-3 bg-white dark:bg-zinc-950"
      data-testid={`email-settings-editor-${account.email}`}
    >
      <FieldRow label="Display label">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Support" data-testid="email-edit-label"/>
      </FieldRow>

      <FieldRow label="Alias of (leave blank for standalone)">
        <select
          value={aliasOf}
          onChange={(e) => setAliasOf(e.target.value)}
          className="h-9 w-full rounded border border-slate-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-2 text-xs"
          data-testid="email-edit-alias-of"
        >
          <option value="">— Standalone mailbox —</option>
          {otherParents.map((p) => (<option key={p} value={p}>{p}</option>))}
        </select>
      </FieldRow>

      {!isAlias && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <FieldRow label="IMAP host">
              <Input value={imapHost} onChange={(e) => setImapHost(e.target.value)} data-testid="email-edit-imap-host"/>
            </FieldRow>
            <FieldRow label="IMAP port">
              <Input value={imapPort} onChange={(e) => setImapPort(e.target.value)} type="number" data-testid="email-edit-imap-port"/>
            </FieldRow>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <FieldRow label="SMTP host">
              <Input value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} data-testid="email-edit-smtp-host"/>
            </FieldRow>
            <FieldRow label="SMTP port">
              <Input value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} type="number" data-testid="email-edit-smtp-port"/>
            </FieldRow>
          </div>
          <FieldRow label="Reset password (leave blank to keep current)">
            <Input
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              type="password"
              placeholder="••••••••"
              autoComplete="new-password"
              data-testid="email-edit-password"
            />
          </FieldRow>
        </>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <Button
          type="submit"
          disabled={busy}
          className="bg-sky-600 hover:bg-sky-500 text-white font-bold h-8 text-xs gap-1.5"
          data-testid="email-edit-save"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin"/> : <Check className="h-3 w-3"/>}
          Save changes
        </Button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────
// AddAccountForm — inline create
// ─────────────────────────────────────────────────────────────────────
function AddAccountForm({ parentEmails, onCancel, onSaved }) {
  const [email, setEmail] = useState("");
  const [label, setLabel] = useState("");
  const [aliasOf, setAliasOf] = useState("");
  const [password, setPassword] = useState("");
  const [imapHost, setImapHost] = useState(IONOS_DEFAULTS.imap_host);
  const [imapPort, setImapPort] = useState(String(IONOS_DEFAULTS.imap_port));
  const [smtpHost, setSmtpHost] = useState(IONOS_DEFAULTS.smtp_host);
  const [smtpPort, setSmtpPort] = useState(String(IONOS_DEFAULTS.smtp_port));
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Email is required"); return; }
    if (!aliasOf && !password) { toast.error("Password is required for a standalone mailbox (or pick an alias parent)"); return; }
    setBusy(true);
    try {
      const payload = {
        email: email.trim().toLowerCase(),
        label: label || email.trim().split("@")[0],
        imap_host: imapHost,
        imap_port: parseInt(imapPort, 10) || IONOS_DEFAULTS.imap_port,
        smtp_host: smtpHost,
        smtp_port: parseInt(smtpPort, 10) || IONOS_DEFAULTS.smtp_port,
      };
      if (aliasOf) payload.alias_of_email = aliasOf;
      if (password) payload.password = password;
      await adminApi.post("/admin/email/accounts", payload);
      toast.success(`Added ${payload.email}`);
      onSaved();
    } catch (err) {
      toast.error(`Add failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="border border-sky-300 dark:border-sky-700 rounded p-3 bg-sky-50 dark:bg-sky-900/20 space-y-3"
      data-testid="email-settings-add-form"
    >
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-sky-600 dark:text-sky-300"/>
        <div className="text-sm font-bold text-slate-900 dark:text-zinc-100">Add a mailbox</div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <FieldRow label="Email address" required>
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            placeholder="alias@bullertaxi.com"
            data-testid="email-add-email"
          />
        </FieldRow>
        <FieldRow label="Display label">
          <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Support" data-testid="email-add-label"/>
        </FieldRow>
      </div>

      <FieldRow label="Alias of (optional)">
        <select
          value={aliasOf}
          onChange={(e) => setAliasOf(e.target.value)}
          className="h-9 w-full rounded border border-slate-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-2 text-xs"
          data-testid="email-add-alias-of"
        >
          <option value="">— Standalone mailbox (own IMAP/SMTP login) —</option>
          {parentEmails.map((p) => (<option key={p} value={p}>{p}</option>))}
        </select>
      </FieldRow>

      {!aliasOf && (
        <>
          <FieldRow label="Password" required>
            <Input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type="password"
              placeholder="IONOS mailbox password"
              autoComplete="new-password"
              data-testid="email-add-password"
            />
          </FieldRow>
          <div className="grid grid-cols-2 gap-2">
            <FieldRow label="IMAP host">
              <Input value={imapHost} onChange={(e) => setImapHost(e.target.value)} data-testid="email-add-imap-host"/>
            </FieldRow>
            <FieldRow label="IMAP port">
              <Input value={imapPort} onChange={(e) => setImapPort(e.target.value)} type="number" data-testid="email-add-imap-port"/>
            </FieldRow>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <FieldRow label="SMTP host">
              <Input value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} data-testid="email-add-smtp-host"/>
            </FieldRow>
            <FieldRow label="SMTP port">
              <Input value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} type="number" data-testid="email-add-smtp-port"/>
            </FieldRow>
          </div>
        </>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <Button
          type="button" variant="ghost" size="sm"
          onClick={onCancel}
          className="h-8 text-xs"
          data-testid="email-add-cancel"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={busy}
          className="bg-sky-600 hover:bg-sky-500 text-white font-bold h-8 text-xs gap-1.5"
          data-testid="email-add-save"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin"/> : <Plus className="h-3 w-3"/>}
          Add mailbox
        </Button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────
function FieldRow({ label, required = false, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400">
        {label} {required && <span className="text-rose-500">*</span>}
      </Label>
      {children}
    </div>
  );
}
